/**
 * The work hierarchy as an expandable tree.
 *
 * `client > application > project > feature` is four levels deep and the shape
 * of the data IS the information - which client an application belongs to, how
 * many features a project carries, where the blocked work is. A flat table of
 * any one level loses that, and four tabs makes you hold the parent in your
 * head. So: one tree, expanded to the level you are working at.
 *
 * Depth is carried by indentation and by type, never by colour. Each level
 * steps down one type size and one weight, and the rail down the left of a
 * nested list says where a child belongs. That survives greyscale, which is
 * how a screenshot of this ends up in a status email.
 *
 * This file renders and derives; it never writes. Every action is a callback
 * the page turns into a dialog.
 */
import type { ReactNode } from 'react';

import type { ApplicationDto, ClientDto, FeatureDto, ProjectDto } from '@botguide/shared';
import { roundPoints } from '@botguide/shared';

import { Badge, Button, Icon, LaneBadge, ProgressBar } from '@/components';
import { cn } from '@/lib/cn';
import { EMPTY, formatCount, formatDayShort, formatPoints, formatProgress } from '@/lib/format';

/* -------------------------------------------------------------------------- */
/* Shape                                                                      */
/* -------------------------------------------------------------------------- */

export interface ProjectNode {
  project: ProjectDto;
  features: FeatureDto[];
}

export interface ApplicationNode {
  application: ApplicationDto;
  projects: ProjectNode[];
}

export interface ClientNode {
  client: ClientDto;
  applications: ApplicationNode[];
}

/** A node the page has been asked to edit or delete. */
export type TreeTarget =
  | { kind: 'client'; node: ClientNode }
  | { kind: 'application'; node: ApplicationNode }
  | { kind: 'project'; node: ProjectNode }
  | { kind: 'feature'; node: FeatureDto };

/** A child the page has been asked to create, and what it hangs off. */
export type CreateTarget =
  | { kind: 'client' }
  | { kind: 'application'; parentId: string; parentName: string }
  | { kind: 'project'; parentId: string; parentName: string }
  | { kind: 'feature'; parentId: string; parentName: string };

export type EntityKind = TreeTarget['kind'];

export const ENTITY_LABELS: Record<EntityKind, string> = {
  client: 'Client',
  application: 'Application',
  project: 'Project',
  feature: 'Feature',
};

/** The identity a node is keyed by in the expanded set. */
export function nodeKey(target: TreeTarget): string {
  return `${target.kind}:${entityOf(target).id}`;
}

/** The DTO behind a target, for prefilling an edit form. */
export function entityOf(
  target: TreeTarget,
): ClientDto | ApplicationDto | ProjectDto | FeatureDto {
  switch (target.kind) {
    case 'client':
      return target.node.client;
    case 'application':
      return target.node.application;
    case 'project':
      return target.node.project;
    case 'feature':
      return target.node;
  }
}

export function nameOf(target: TreeTarget): string {
  return entityOf(target).name;
}

/* -------------------------------------------------------------------------- */
/* Derivation                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Everything under a node, counted.
 *
 * Two consumers need exactly this: the rollup printed on a row, and the
 * confirmation that says what a delete is about to take with it. One
 * derivation, so the number you are warned about is the number you were shown.
 */
export interface Totals {
  applications: number;
  projects: number;
  features: number;
  tasks: number;
  completedTasks: number;
  blocked: number;
  totalPoints: number;
  completedPoints: number;
  /** 0-1, points-weighted where points exist, exactly as `rollUpTasks` does. */
  progress: number;
}

function aggregate(
  applications: number,
  projects: number,
  features: readonly FeatureDto[],
): Totals {
  let tasks = 0;
  let completedTasks = 0;
  let blocked = 0;
  let totalPoints = 0;
  let completedPoints = 0;

  for (const { rollup } of features) {
    tasks += rollup.taskCount;
    completedTasks += rollup.completedCount;
    blocked += rollup.blockedCount;
    totalPoints += rollup.totalPoints;
    completedPoints += rollup.completedPoints;
  }

  const progress =
    totalPoints > 0
      ? completedPoints / totalPoints
      : tasks > 0
        ? completedTasks / tasks
        : 0;

  return {
    applications,
    projects,
    features: features.length,
    tasks,
    completedTasks,
    blocked,
    totalPoints: roundPoints(totalPoints),
    completedPoints: roundPoints(completedPoints),
    progress: Math.min(1, Math.max(0, progress)),
  };
}

function featuresOfApplication(node: ApplicationNode): FeatureDto[] {
  return node.projects.flatMap((project) => project.features);
}

function featuresOfClient(node: ClientNode): FeatureDto[] {
  return node.applications.flatMap(featuresOfApplication);
}

export function totalsFor(target: TreeTarget): Totals {
  switch (target.kind) {
    case 'client':
      return aggregate(
        target.node.applications.length,
        target.node.applications.reduce((n, app) => n + app.projects.length, 0),
        featuresOfClient(target.node),
      );
    case 'application':
      return aggregate(0, target.node.projects.length, featuresOfApplication(target.node));
    case 'project':
      return aggregate(0, 0, target.node.features);
    case 'feature':
      return aggregate(0, 0, [target.node]);
  }
}

/** Fold four flat lists into the hierarchy, preserving each list's order. */
export function buildTree(
  clients: readonly ClientDto[],
  applications: readonly ApplicationDto[],
  projects: readonly ProjectDto[],
  features: readonly FeatureDto[],
): ClientNode[] {
  const featuresByProject = new Map<string, FeatureDto[]>();
  for (const feature of features) {
    const list = featuresByProject.get(feature.projectId);
    if (list) list.push(feature);
    else featuresByProject.set(feature.projectId, [feature]);
  }

  const projectsByApplication = new Map<string, ProjectNode[]>();
  for (const project of projects) {
    const node: ProjectNode = { project, features: featuresByProject.get(project.id) ?? [] };
    const list = projectsByApplication.get(project.applicationId);
    if (list) list.push(node);
    else projectsByApplication.set(project.applicationId, [node]);
  }

  const applicationsByClient = new Map<string, ApplicationNode[]>();
  for (const application of applications) {
    const node: ApplicationNode = {
      application,
      projects: projectsByApplication.get(application.id) ?? [],
    };
    const list = applicationsByClient.get(application.clientId);
    if (list) list.push(node);
    else applicationsByClient.set(application.clientId, [node]);
  }

  return clients.map((client) => ({
    client,
    applications: applicationsByClient.get(client.id) ?? [],
  }));
}

/* -------------------------------------------------------------------------- */
/* The tree                                                                   */
/* -------------------------------------------------------------------------- */

export interface EntityTreeProps {
  clients: readonly ClientNode[];
  /** Keys from `nodeKey`. The page owns this so a write can keep it open. */
  expanded: ReadonlySet<string>;
  onToggle: (key: string) => void;
  /** False hides every control the API would refuse. Cosmetic - see session.tsx. */
  canWrite: boolean;
  onCreate: (target: CreateTarget) => void;
  /**
   * Adding a task under a feature.
   *
   * Separate from `onCreate` because a task is not one of this tree's four
   * entities: it carries a lane, a stage, an assignee, story points and a
   * blocked flag, and it is created through `TaskDialog`. Folding it into
   * `CreateTarget` would oblige `EntityDialog` to render a form it has no
   * business knowing about.
   */
  onCreateTask: (feature: FeatureDto) => void;
  onEdit: (target: TreeTarget) => void;
  onDelete: (target: TreeTarget) => void;
}

export function EntityTree({
  clients,
  expanded,
  onToggle,
  canWrite,
  onCreate,
  onCreateTask,
  onEdit,
  onDelete,
}: EntityTreeProps) {
  const shared = { expanded, onToggle, canWrite, onCreate, onCreateTask, onEdit, onDelete };

  return (
    <ul className="flex flex-col gap-2" role="tree" aria-label="Work hierarchy">
      {clients.map((node) => (
        <li key={node.client.id} role="none" className="rounded-lg border border-line bg-surface">
          <ClientBranch node={node} {...shared} />
        </li>
      ))}
    </ul>
  );
}

type BranchProps = Omit<EntityTreeProps, 'clients'>;

function ClientBranch({ node, ...rest }: BranchProps & { node: ClientNode }) {
  const target = { kind: 'client', node } as const;
  const open = rest.expanded.has(nodeKey(target));
  const totals = totalsFor(target);
  const addTarget = childTargetFor(target);

  return (
    <div>
      <NodeRow
        target={target}
        depth={0}
        open={open}
        totals={totals}
        childLabel={formatCount(totals.applications, 'application')}
        addLabel="Add application"
        addTarget={addTarget}
        secondary={node.client.isActive ? node.client.slug : `${node.client.slug} · archived`}
        muted={!node.client.isActive}
        {...rest}
      />
      {open ? (
        <Children
          empty={
            <EmptyBranch
              what="applications"
              hint="An application is the product a client is paying for — spend and ROI roll up to it."
              onAdd={rest.canWrite ? () => rest.onCreate(addTarget) : undefined}
              addLabel="Add application"
            />
          }
        >
          {node.applications.map((child) => (
            <li key={child.application.id} role="none">
              <ApplicationBranch node={child} {...rest} />
            </li>
          ))}
        </Children>
      ) : null}
    </div>
  );
}

function ApplicationBranch({ node, ...rest }: BranchProps & { node: ApplicationNode }) {
  const target = { kind: 'application', node } as const;
  const open = rest.expanded.has(nodeKey(target));
  const totals = totalsFor(target);
  const addTarget = childTargetFor(target);

  return (
    <div>
      <NodeRow
        target={target}
        depth={1}
        open={open}
        totals={totals}
        childLabel={formatCount(totals.projects, 'project')}
        addLabel="Add project"
        addTarget={addTarget}
        secondary={node.application.isActive ? node.application.slug : `${node.application.slug} · archived`}
        muted={!node.application.isActive}
        {...rest}
      />
      {open ? (
        <Children
          empty={
            <EmptyBranch
              what="projects"
              hint="A project is one month's engagement against this application."
              onAdd={rest.canWrite ? () => rest.onCreate(addTarget) : undefined}
              addLabel="Add project"
            />
          }
        >
          {node.projects.map((child) => (
            <li key={child.project.id} role="none">
              <ProjectBranch node={child} {...rest} />
            </li>
          ))}
        </Children>
      ) : null}
    </div>
  );
}

function ProjectBranch({ node, ...rest }: BranchProps & { node: ProjectNode }) {
  const target = { kind: 'project', node } as const;
  const open = rest.expanded.has(nodeKey(target));
  const totals = totalsFor(target);
  const addTarget = childTargetFor(target);

  const window_ =
    node.project.startsOn || node.project.targetOn
      ? `${formatDayShort(node.project.startsOn)} → ${formatDayShort(node.project.targetOn)}`
      : 'No dates set';

  return (
    <div>
      <NodeRow
        target={target}
        depth={2}
        open={open}
        totals={totals}
        childLabel={formatCount(totals.features, 'feature')}
        addLabel="Add feature"
        addTarget={addTarget}
        secondary={node.project.isArchived ? `${window_} · archived` : window_}
        muted={node.project.isArchived}
        {...rest}
      />
      {open ? (
        <Children
          empty={
            <EmptyBranch
              what="features"
              hint="A feature is what the client is told they are getting. Tasks hang off it on the board."
              onAdd={rest.canWrite ? () => rest.onCreate(addTarget) : undefined}
              addLabel="Add feature"
            />
          }
        >
          {node.features.map((feature) => (
            <li key={feature.id} role="none">
              <FeatureBranch node={feature} {...rest} />
            </li>
          ))}
        </Children>
      ) : null}
    </div>
  );
}

function FeatureBranch({ node, ...rest }: BranchProps & { node: FeatureDto }) {
  const target: TreeTarget = { kind: 'feature', node };
  const open = rest.expanded.has(nodeKey(target));

  return (
    <div>
      <NodeRow
        target={target}
        depth={3}
        open={open}
        totals={totalsFor(target)}
        childLabel={formatCount(node.rollup.taskCount, 'task')}
        addLabel="Add task"
        onAdd={rest.canWrite ? () => rest.onCreateTask(node) : undefined}
        secondary={null}
        {...rest}
      />
      {open ? (
        <div className="ms-2 border-s border-line ps-3 sm:ms-4 sm:ps-4">
          <dl className="flex flex-col gap-2 py-3 pe-2 text-[0.8125rem] sm:pe-4">
            <Detail term="Summary">{node.summary || <Unset />}</Detail>
            <Detail term="Client outcome">{node.clientOutcome || <Unset />}</Detail>
            <Detail term="Points">
              <span className="font-mono tnum">
                {formatPoints(node.rollup.completedPoints)} of {formatPoints(node.rollup.totalPoints)}{' '}
                complete
              </span>
            </Detail>
          </dl>

          {/*
            Said here because a feature with no tasks is where people get
            stuck: hours hang off a task, so an empty feature offers nothing
            on My Hours and the screen gives no reason.
          */}
          {node.rollup.taskCount === 0 && rest.canWrite ? (
            <div className="pb-3 pe-2 sm:pe-4">
              <Button size="sm" variant="secondary" iconLeft="plus" onClick={() => rest.onCreateTask(node)}>
                Add the first task
              </Button>
              <p className="mt-2 text-[0.8125rem] text-muted">
                Hours are logged against a task, never a feature.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Row                                                                        */
/* -------------------------------------------------------------------------- */

interface NodeRowProps extends BranchProps {
  target: TreeTarget;
  depth: 0 | 1 | 2 | 3;
  open: boolean;
  totals: Totals;
  /** "3 projects", "11 tasks" - the count of the thing one level down. */
  childLabel: string;
  addLabel?: string;
  /** For the four entity levels. A feature supplies `onAdd` instead. */
  addTarget?: CreateTarget;
  /** Takes precedence over `addTarget`, for children this tree does not own. */
  onAdd?: () => void;
  secondary: ReactNode;
  muted?: boolean;
}

/** One line of the ledger: what it is, how much is under it, and how far along. */
function NodeRow({
  target,
  depth,
  open,
  totals,
  childLabel,
  addLabel,
  addTarget,
  onAdd,
  secondary,
  muted = false,
  canWrite,
  onToggle,
  onCreate,
  onEdit,
  onDelete,
}: NodeRowProps) {
  const feature = target.kind === 'feature' ? target.node : null;
  const name = nameOf(target);

  return (
    <div
      className={cn(
        'flex items-start gap-1.5 px-2 py-2 sm:gap-2 sm:px-3',
        depth > 0 && 'border-t border-line',
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(nodeKey(target))}
        aria-expanded={open}
        className="group flex min-w-0 flex-1 items-start gap-2 rounded-sm text-start"
      >
        <span
          className={cn(
            'mt-0.5 grid size-5 shrink-0 place-items-center rounded-xs text-subtle transition-transform duration-150',
            'group-hover:text-ink',
            open && 'rotate-90',
          )}
          aria-hidden="true"
        >
          <Icon name="chevronRight" size={14} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className={cn(
                'truncate',
                depth === 0 && 'font-display text-[1.0625rem] font-semibold tracking-[-0.01em]',
                depth === 1 && 'text-[0.9375rem] font-semibold',
                depth === 2 && 'text-sm font-medium',
                depth === 3 && 'text-sm',
                muted && 'text-muted',
              )}
            >
              {name}
            </span>
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.14em] text-subtle">
              {ENTITY_LABELS[target.kind]}
            </span>
          </span>

          {secondary ? (
            <span className="mt-0.5 block truncate text-[0.75rem] text-subtle">{secondary}</span>
          ) : null}

          {/* The rollup. Under the name on a phone, beside it from `sm`. */}
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {feature ? <LaneBadge lane={feature.rollup.lane} size="sm" /> : null}

            <span className="font-mono text-[0.6875rem] text-subtle tnum">{childLabel}</span>

            {target.kind !== 'feature' && totals.tasks > 0 ? (
              <span className="font-mono text-[0.6875rem] text-subtle tnum">
                {formatCount(totals.tasks, 'task')}
              </span>
            ) : null}

            {totals.blocked > 0 ? (
              <Badge tone="danger" size="sm" icon="alert">
                {totals.blocked} blocked
              </Badge>
            ) : null}

            {totals.tasks > 0 ? (
              <span className="flex min-w-24 max-w-40 flex-1 items-center gap-2">
                <ProgressBar
                  value={totals.progress}
                  size="sm"
                  className="flex-1"
                  blockedFraction={totals.tasks > 0 ? totals.blocked / totals.tasks : 0}
                />
                <span className="font-mono text-[0.6875rem] text-muted tnum">
                  {formatProgress(totals.progress)}
                </span>
              </span>
            ) : null}
          </span>
        </span>
      </button>

      {canWrite ? (
        <span className="flex shrink-0 items-center gap-0.5">
          {addLabel && (onAdd || addTarget) ? (
            <Button
              variant="ghost"
              size="sm"
              className="size-8 px-0"
              title={addLabel}
              onClick={() => (onAdd ? onAdd() : onCreate(addTarget!))}
            >
              <Icon name="plus" size={15} />
              <span className="sr-only">
                {addLabel} to {name}
              </span>
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-[0.6875rem]"
            onClick={() => onEdit(target)}
          >
            Edit<span className="sr-only"> {name}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 text-[0.6875rem] hover:text-danger-ink"
            onClick={() => onDelete(target)}
          >
            Delete<span className="sr-only"> {name}</span>
          </Button>
        </span>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Small parts                                                                */
/* -------------------------------------------------------------------------- */

/** The indent rail. One rule per level is what makes the nesting readable. */
function Children({ empty, children }: { empty: ReactNode; children: ReactNode[] }) {
  return (
    <div className="ms-2 border-s border-line ps-1 sm:ms-4 sm:ps-2">
      {children.length === 0 ? (
        empty
      ) : (
        <ul role="group" className="flex flex-col">
          {children}
        </ul>
      )}
    </div>
  );
}

function EmptyBranch({
  what,
  hint,
  onAdd,
  addLabel,
}: {
  what: string;
  hint: string;
  onAdd?: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-3 py-3">
      <p className="min-w-0 max-w-md text-[0.8125rem] text-subtle">
        <span className="font-medium text-muted">No {what} yet.</span> {hint}
      </p>
      {onAdd ? (
        <Button variant="secondary" size="sm" iconLeft="plus" onClick={onAdd}>
          {addLabel}
        </Button>
      ) : null}
    </div>
  );
}

function Detail({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="shrink-0 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-subtle sm:w-32 sm:pt-0.5">
        {term}
      </dt>
      <dd className="min-w-0 text-muted">{children}</dd>
    </div>
  );
}

function Unset() {
  return <span className="text-subtle">{EMPTY} not written yet</span>;
}

/**
 * The create target for a node's children, so `+` means the same everywhere.
 *
 * A feature is excluded at the type level rather than handled and ignored: its
 * children are tasks, and tasks are created on the board, not here.
 */
type ParentTarget = Exclude<TreeTarget, { kind: 'feature' }>;

function childTargetFor(target: ParentTarget): CreateTarget {
  switch (target.kind) {
    case 'client':
      return {
        kind: 'application',
        parentId: target.node.client.id,
        parentName: target.node.client.name,
      };
    case 'application':
      return {
        kind: 'project',
        parentId: target.node.application.id,
        parentName: target.node.application.name,
      };
    case 'project':
      return {
        kind: 'feature',
        parentId: target.node.project.id,
        parentName: target.node.project.name,
      };
  }
}
